import { useEffect, useMemo, useReducer, type ReactNode } from 'react';
import type { MooChatConfig } from '../types';
import type { AnalyticsAdapter, LoggerAdapter } from '../types/adapters';
import { MooChatContext } from './internal-context';
import { initialShellState } from '../state/shell-state';
import { shellReducer } from '../state/shell-reducer';
import { useStorageHydration } from '../storage/use-storage-hydration';

export interface MooChatProviderProps {
  config: MooChatConfig;
  children: ReactNode;
}

/** Wraps the host-supplied analytics adapter so a throwing `track` cannot
 *  bubble out of a library call site. Per architecture: adapters are
 *  non-critical — a broken analytics path must never break the chat.
 *  Failures are logged once via the host's logger as `moo.analytics.error`. */
function makeSafeAnalytics(
  analytics: AnalyticsAdapter,
  logger: LoggerAdapter,
): AnalyticsAdapter {
  return {
    track: (event, props) => {
      try {
        analytics.track(event, props);
      } catch (err) {
        logger.warn('moo.analytics.error', { event, error: String(err) });
      }
    },
    identify: analytics.identify
      ? (userId, traits) => {
          try {
            analytics.identify?.(userId, traits);
          } catch (err) {
            logger.warn('moo.analytics.error', {
              event: 'identify',
              error: String(err),
            });
          }
        }
      : undefined,
  };
}

export function MooChatProvider({
  config,
  children,
}: Readonly<MooChatProviderProps>) {
  const [state, dispatch] = useReducer(shellReducer, initialShellState);

  const safeConfig = useMemo<MooChatConfig>(
    () => ({
      ...config,
      analytics: makeSafeAnalytics(config.analytics, config.logger),
    }),
    [config],
  );

  useEffect(() => {
    safeConfig.logger.info('moo.lifecycle.provider-mounted');
    return () => {
      safeConfig.logger.info('moo.lifecycle.provider-unmounted');
    };
  }, [safeConfig]);

  const value = useMemo(
    () => ({ config: safeConfig, state, dispatch }),
    [safeConfig, state],
  );

  return (
    <MooChatContext.Provider value={value}>
      <StorageHydrationGate />
      {children}
    </MooChatContext.Provider>
  );
}

/** Tiny child that mounts the storage hook below the provider so the
 *  hook can see the context value. Lives at provider scope (sibling to
 *  `children`) so it stays mounted across launcher open/close — that's
 *  what keeps `hasHydrated` from resetting and re-overwriting state
 *  when the user re-opens the chat. */
function StorageHydrationGate() {
  useStorageHydration();
  return null;
}
